import React, { useEffect, useState } from "react";
import { Col, Form, FormGroup, Input } from "reactstrap";

const DateRangeSelector = ({ onApply, showReload = false, defaultRange = null }) => {
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const now = new Date();
    const day = now.getDate();
    const month = now.getMonth();
    const year = now.getFullYear();

    // Helper to format half label (Added Year to fix ambiguity)
    const halfLabel = (y, m, half) => `${monthNames[m]} ${half} ${y}`;

    const fullMonthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    // Calculate 2nd and 3rd previous months full names with year
    const getPrevMonthFullLabel = (sub) => {
        const d = new Date(year, month - sub, 1);
        return `${fullMonthNames[d.getMonth()]} ${d.getFullYear()}`;
    };
    const month2BackLabel = getPrevMonthFullLabel(2); // e.g. "November 2025" if current is Jan 2026
    const month3BackLabel = getPrevMonthFullLabel(3); // e.g. "October 2025"

    // Decide the "active" half (previous half depending on current position)
    let currentHalf;
    if (day <= 15) {
        // first half of current month -> pick previous half
        if (month === 0) {
            currentHalf = { y: year - 1, m: 11, half: "2H" }; // Dec 2H of last year
        } else {
            currentHalf = { y: year, m: month - 1, half: "2H" };
        }
    } else {
        // second half of current month -> pick first half of current month
        currentHalf = { y: year, m: month, half: "1H" };
    }

    // Build last 3 half options
    const buildHalves = (base) => {
        const res = [];
        let { y, m, half } = base;

        for (let i = 0; i < 3; i++) {
            res.unshift(halfLabel(y, m, half)); // only label needed
            // step back one half
            if (half === "1H") {
                half = "2H";
                m = (m - 1 + 12) % 12;
                if (m === 11) y--;
            } else {
                half = "1H";
            }
        }
        return res;
    };

    const options = buildHalves(currentHalf);
    const [selectedRange, setSelectedRange] = useState(defaultRange || halfLabel(currentHalf.y, currentHalf.m, currentHalf.half));

    useEffect(() => {
        const initialRange = defaultRange || halfLabel(currentHalf.y, currentHalf.m, currentHalf.half);
        const { start, end } = computeRange(initialRange);
        setStartDate(start);
        setEndDate(end);
        onApply(start, end);
    }, []); // run once

    // compute ranges inside here
    const computeRange = (range) => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth(); // 0-11
        let start, end;

        if (range === "Today") {
            start = now.toISOString().slice(0, 10);
            end = now.toISOString().slice(0, 10);
        }
        else if (range === "This Week") {
            const day = now.getDay();
            const diffToMonday = now.getDate() - day + (day === 0 ? -6 : 1);
            const monday = new Date(now.setDate(diffToMonday));
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);
            start = monday.toISOString().slice(0, 10);
            end = sunday.toISOString().slice(0, 10);
        }
        else if (range === "This Month") {
            const first = new Date(currentYear, currentMonth, 1);
            const last = new Date(currentYear, currentMonth + 1, 0);
            start = first.toISOString().slice(0, 10);
            end = last.toISOString().slice(0, 10);
        }
        else if (range === "Last Month") {
            const first = new Date(currentYear, currentMonth - 1, 1);
            const last = new Date(currentYear, currentMonth, 0);
            start = first.toISOString().slice(0, 10);
            end = last.toISOString().slice(0, 10);
        }
        else if (range === "This Year 1H") {
            const first = new Date(currentYear, 0, 1);
            const mid = new Date(currentYear, 5, 30);
            start = first.toISOString().slice(0, 10);
            end = mid.toISOString().slice(0, 10);
        }
        else if (range === "This Year 2H") {
            const mid = new Date(currentYear, 6, 1);
            const last = new Date(currentYear, 11, 31);
            start = mid.toISOString().slice(0, 10);
            end = last.toISOString().slice(0, 10);
        }
        else if (range === "This Year") {
            const first = new Date(currentYear, 0, 1);
            const last = new Date(currentYear, 11, 31);
            start = first.toISOString().slice(0, 10);
            end = last.toISOString().slice(0, 10);
        }
        else if (range === "Last Year") {
            const first = new Date(currentYear - 1, 0, 1);
            const last = new Date(currentYear - 1, 11, 31);
            start = first.toISOString().slice(0, 10);
            end = last.toISOString().slice(0, 10);
        }
        else if (range === "Last 5 Years") {
            const first = new Date(currentYear - 5, 0, 1);
            const last = new Date(currentYear, 11, 31);
            start = first.toISOString().slice(0, 10);
            end = last.toISOString().slice(0, 10);
        }
        else if (range === "Last 3 Months") {
            const first = new Date(currentYear, currentMonth - 3, 1);
            const last = new Date(currentYear, currentMonth, 0);
            start = first.toISOString().slice(0, 10);
            end = last.toISOString().slice(0, 10);
        }
        // DYNAMIC PARSING: Handles specific month ranges
        else {
            const parts = range.split(" ");

            // Case 1: "Dec 2H 2025" (3 parts)
            if (parts.length === 3 && monthNames.includes(parts[0])) {
                const mIndex = monthNames.indexOf(parts[0]);
                const targetYear = parseInt(parts[2]);
                const isFirstHalf = parts[1] === "1H";

                if (!isNaN(targetYear) && mIndex !== -1) {
                    if (isFirstHalf) {
                        start = new Date(targetYear, mIndex, 1).toISOString().slice(0, 10);
                        end = new Date(targetYear, mIndex, 15).toISOString().slice(0, 10);
                    } else {
                        start = new Date(targetYear, mIndex, 16).toISOString().slice(0, 10);
                        end = new Date(targetYear, mIndex + 1, 0).toISOString().slice(0, 10);
                    }
                }
            }
            // Case 2: "October 2025" (2 parts) - Full Month
            else if (parts.length === 2 && fullMonthNames.includes(parts[0])) {
                const mIndex = fullMonthNames.indexOf(parts[0]);
                const targetYear = parseInt(parts[1]);

                if (!isNaN(targetYear) && mIndex !== -1) {
                    start = new Date(targetYear, mIndex, 1).toISOString().slice(0, 10);
                    end = new Date(targetYear, mIndex + 1, 0).toISOString().slice(0, 10);
                }
            }
        }

        return { start, end };
    };

    // when selectedRange changes, auto compute and send up
    useEffect(() => {
        if (selectedRange !== "Choose Date Range") {
            const { start, end } = computeRange(selectedRange);
            setStartDate(start);
            setEndDate(end);
            onApply(start, end);
        }
    }, [selectedRange]);

    return (
        <Form className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-3">
            <FormGroup className="d-flex align-items-center gap-2 mb-0 w-100 w-md-auto">
                <Input
                    id="dateRange"
                    type="select"
                    className="form-select form-select-lg accordion-gradient-border"
                    style={{ minWidth: "220px", width: "100%" }}
                    value={selectedRange}
                    onChange={(e) => setSelectedRange(e.target.value)}
                >
                    {options.map(opt => (
                        <option key={opt}>{opt}</option>
                    ))}
                    <option>This Month</option>
                    <option>Last Month</option>
                    <option>{month2BackLabel}</option>
                    <option>{month3BackLabel}</option>
                    <option>This Week</option>
                    <option>Today</option>
                    <option>Last 3 Months</option>
                    <option>This Year 1H</option>
                    <option>This Year 2H</option>
                    <option>This Year</option>
                    <option>Last Year</option>
                    <option>Last 5 Years</option>
                    <option>Choose Date Range</option>
                </Input>
                {showReload && (
                    <button
                        type="button"
                        className="btn btn-link p-0 ms-2"
                        onClick={() => {
                            const { start, end } = computeRange(selectedRange);
                            onApply(start, end);
                        }}
                    >
                        <i className="bx bx-doughnut-chart bx-spin-hover gradient-font-color" style={{ fontSize: "1.9rem", marginTop: 5 }}></i>
                    </button>
                )}
            </FormGroup>

            {selectedRange === "Choose Date Range" && (
                <>
                    <FormGroup className="d-flex align-items-center gap-2 mb-0 w-100 w-md-auto">
                        <Input
                            id="startDate"
                            type="date"
                            className="form-control form-control-lg accordion-gradient-border"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            max={endDate}
                            style={{ width: "100%" }}
                        />
                    </FormGroup>
                    <FormGroup className="d-flex align-items-center gap-2 mb-0 w-100 w-md-auto">
                        <Input
                            id="endDate"
                            type="date"
                            className="form-control form-control-lg accordion-gradient-border"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            style={{ width: "100%" }}
                        />
                    </FormGroup>
                    <div className="w-100 w-md-auto text-center">
                        <button
                            type="button"
                            className="button-gradient-bg w-100"
                            style={{ minWidth: "80px" }}
                            onClick={() => onApply(startDate, endDate)}
                        >
                            Apply
                        </button>
                    </div>
                </>
            )}
        </Form>
    );
};

export default DateRangeSelector;